import type { AppointmentReportAnswers } from '../../api/types';
import { cancelNextComplete } from '../cancelNext';

// Fragenkatalog nach dem Termin (OFFENE_FRAGEN B-29 Phase 2): welche Schritte je nach Antwort erscheinen und wann ein Schritt
// vollständig ist. Jede Frage baut auf der vorigen auf (Dominik 12.10.2026): Kam kein Gespräch zustande, folgen Grund und
// weiteres Vorgehen – keine Fragen nach Preisvorstellung oder Vereinbarungen. Gegenstück im Backend: reports.Normalize/Plan.

export type Answers = AppointmentReportAnswers;
export type StepKey = 'held' | 'notPossible' | 'noShow' | 'mood' | 'interest' | 'facts' | 'agreements' | 'summary';

export const stepTitles: Record<StepKey, string> = {
  held: 'Wie ist der Termin gelaufen?',
  notPossible: 'Woran ist das Gespräch gescheitert?',
  noShow: 'Wie geht es weiter?',
  mood: 'Wie ist das Gespräch verlaufen?',
  interest: 'Besteht konkretes Interesse?',
  facts: 'Welche Angaben haben Sie aufgenommen?',
  agreements: 'Was wurde vereinbart?',
  summary: 'Zusammenfassung – das passiert jetzt',
};

export const heldOptions = [
  { value: 'HELD', label: 'Gespräch geführt' },
  { value: 'NOT_POSSIBLE', label: 'Vor Ort, aber kein Gespräch', hint: 'z. B. nicht der Eigentümer da, kein Gespräch gewünscht' },
  { value: 'NO_SHOW', label: 'Kunde nicht angetroffen' },
  { value: 'RESCHEDULED', label: 'Vor Ort verschoben', hint: 'Neuen Termin direkt mit dem Kunden vereinbart' },
  { value: 'CANCELED', label: 'Kunde hat abgesagt' },
  { value: 'BROKER_MISSED', label: 'Ich konnte nicht hin', hint: 'Der Terminierer ruft den Kunden heute an' },
] as const;

export const notPossibleOptions = [
  { value: 'NOT_DECISION_MAKER', label: 'Nicht der Eigentümer bzw. Entscheider da' },
  { value: 'NO_TALK', label: 'Kunde wollte kein Gespräch' },
  { value: 'WRONG_DATA', label: 'Adresse oder Objekt stimmte nicht', hint: 'Der Support prüft die Daten' },
  { value: 'TIME', label: 'Zu wenig Zeit, abgebrochen' },
  { value: 'OTHER', label: 'Sonstiges' },
] as const;

export const nextOptions = [
  { value: 'TERMINIERER', label: 'Terminierer vereinbart einen neuen Termin' },
  { value: 'SELF', label: 'Ich melde mich selbst beim Kunden' },
  { value: 'NONE', label: 'Nichts weiter' },
] as const;

// Nach „Vor Ort, aber kein Gespräch“ auch ein Telefontermin (Dominik 13.10.2026: „beides anbieten“) – schon vor Ort abgesprochen
// oder über den Terminierer.
export const notPossibleNextOptions = [
  { value: 'PHONE_SET', label: 'Telefontermin steht schon', hint: 'Datum, Uhrzeit und Gesprächspartner eintragen' },
  { value: 'PHONE', label: 'Terminierer vereinbart einen Telefontermin' },
  { value: 'TERMINIERER', label: 'Terminierer vereinbart einen neuen Termin vor Ort' },
  { value: 'SELF', label: 'Ich melde mich selbst beim Kunden' },
  { value: 'NONE', label: 'Nichts weiter' },
] as const;

export const moodOptions = [
  { value: 'VERY_GOOD', label: 'Sehr gut' },
  { value: 'GOOD', label: 'Gut' },
  { value: 'NEUTRAL', label: 'Neutral' },
  { value: 'DIFFICULT', label: 'Schwierig' },
] as const;

export const difficultyOptions = [
  { value: 'PRICE', label: 'Preisvorstellung' },
  { value: 'MISTRUST', label: 'Misstrauen gegenüber Maklern' },
  { value: 'FAMILY', label: 'Uneinigkeit Familie/Erben' },
  { value: 'TIME', label: 'Zeitdruck' },
  { value: 'OTHER', label: 'Sonstiges' },
] as const;

export const interestOptions = [
  { value: 'MANDATE', label: 'Auftrag erteilt' },
  { value: 'LIKELY', label: 'Auftrag in Aussicht' },
  { value: 'UNDECIDED', label: 'Unentschlossen' },
  { value: 'NONE', label: 'Kein Interesse' },
] as const;

export const missingOptions = [
  { value: 'DOCUMENTS', label: 'Unterlagen' },
  { value: 'VALUATION', label: 'Bewertung/Preis' },
  { value: 'TIME', label: 'Bedenkzeit' },
  { value: 'FAMILY', label: 'Rücksprache Familie/Partner' },
  { value: 'OTHER', label: 'Sonstiges' },
] as const;

export const noInterestOptions = [
  { value: 'PRICE', label: 'Preisvorstellung' },
  { value: 'NO_BROKER', label: 'Will ohne Makler verkaufen' },
  { value: 'NOT_SELLING', label: 'Verkauf abgesagt' },
  { value: 'SOLD', label: 'Schon verkauft / anderer Makler' },
  { value: 'OTHER', label: 'Sonstiges' },
] as const;

type Agreement = NonNullable<Answers['agreements']>[number];
const agreementCatalog: Record<Agreement, { label: string; hint: string }> = {
  DOCUMENTS: { label: 'Kunde schickt Unterlagen', hint: 'Der Support hält sie nach' },
  VALUATION: { label: 'Bewertung erstellen', hint: 'Aufgabe für Sie' },
  EXPOSE: { label: 'Exposé und Fotos', hint: 'Aufgabe für Sie' },
  SECOND_APPOINTMENT: { label: 'Zweiter Termin', hint: 'Der Terminierer vereinbart ihn' },
  CALLBACK: { label: 'Ich rufe den Kunden zurück', hint: 'Ihre Wiedervorlage' },
};

// agreementOptions: Vereinbarungen passend zum Ergebnis – nach erteiltem Auftrag Unterlagen, Bewertung, Exposé/Fotos und ein
// weiterer Termin; ist der Auftrag noch offen, Unterlagen, Bewertung, Zweittermin oder der eigene Rückruf.
export function agreementOptions(interest: Answers['interest']): { value: Agreement; label: string; hint: string }[] {
  const keys: Agreement[] = interest === 'MANDATE' ? ['DOCUMENTS', 'VALUATION', 'EXPOSE', 'SECOND_APPOINTMENT'] : ['DOCUMENTS', 'VALUATION', 'SECOND_APPOINTMENT', 'CALLBACK'];
  return keys.map((value) => {
    const o = agreementCatalog[value];
    return value === 'SECOND_APPOINTMENT' && interest === 'MANDATE' ? { value, label: 'Weiterer Termin (z. B. Fotos, Besichtigung)', hint: o.hint } : { value, ...o };
  });
}

const interested = (a: Answers) => a.held === 'HELD' && (a.interest === 'MANDATE' || a.interest === 'LIKELY' || a.interest === 'UNDECIDED');

// onlyPrice: Kein Interesse wegen der Preisvorstellung – dann wird nur nach ihr gefragt.
export const onlyPrice = (a: Answers) => a.held === 'HELD' && a.interest === 'NONE' && a.no_interest_reason === 'PRICE';

// reportSteps: die Schritte für die bisherigen Antworten – nicht passende Fragen entfallen.
export function reportSteps(a: Answers): StepKey[] {
  switch (a.held) {
    case 'NOT_POSSIBLE':
      return ['held', 'notPossible', 'summary'];
    case 'NO_SHOW':
      return ['held', 'noShow', 'summary'];
    case 'RESCHEDULED':
    case 'CANCELED':
    case 'BROKER_MISSED':
      return ['held', 'summary'];
    case 'HELD': {
      const steps: StepKey[] = ['held', 'mood', 'interest'];
      if (interested(a) || onlyPrice(a)) steps.push('facts');
      if (interested(a)) steps.push('agreements');
      return [...steps, 'summary'];
    }
    default:
      return ['held'];
  }
}

const nextDone = (a: Answers) => !!a.next_by && (a.next_by !== 'SELF' || !!a.self_date) && (a.next_by !== 'PHONE_SET' || !!a.phone_start);

// stepComplete: Ein Schritt ist beantwortet (Weiter möglich).
export function stepComplete(step: StepKey, a: Answers): boolean {
  switch (step) {
    case 'held':
      if (a.held === 'RESCHEDULED') return !!a.new_start;
      if (a.held === 'CANCELED') return !!a.cancel_reason?.trim() && cancelNextComplete(a.cancel_next, a.later_date);
      return !!a.held;
    case 'notPossible':
      if (!a.not_possible_reason) return false;
      if (a.not_possible_reason === 'OTHER' && !a.not_possible_note?.trim()) return false;
      return a.not_possible_reason === 'NO_TALK' || nextDone(a);
    case 'noShow':
      return nextDone(a);
    case 'mood':
      return !!a.mood;
    case 'interest':
      if (a.interest === 'NONE') return !!a.no_interest_reason;
      if (a.interest === 'LIKELY' || a.interest === 'UNDECIDED') return !!a.follow_up_at && !!a.follow_up_by;
      return !!a.interest;
    default:
      return true;
  }
}

// prune: nur die Antworten des gewählten Wegs (wer zurückgeht und anders antwortet, schickt nichts aus dem alten Zweig mit).
export function prune(a: Answers): Answers {
  const out: Answers = { held: a.held, note: a.note, facts: {} };
  const notes: Record<string, string> = {};
  for (const step of reportSteps(a)) {
    const t = noteTargets[step];
    const text = t.kind === 'step' ? a.step_notes?.[t.key]?.trim() : '';
    if (t.kind === 'step' && text) notes[t.key] = text;
  }
  if (Object.keys(notes).length > 0) out.step_notes = notes;
  const next = () => {
    out.next_by = a.next_by;
    if (a.next_by === 'SELF') out.self_date = a.self_date;
  };
  switch (a.held) {
    case 'RESCHEDULED':
      out.new_start = a.new_start;
      break;
    case 'CANCELED':
      out.cancel_reason = a.cancel_reason;
      out.cancel_next = a.cancel_next;
      if (a.cancel_next === 'LATER') out.later_date = a.later_date;
      break;
    case 'NO_SHOW':
      next();
      break;
    case 'NOT_POSSIBLE':
      out.not_possible_reason = a.not_possible_reason;
      out.not_possible_note = a.not_possible_note;
      if (a.not_possible_reason === 'NO_TALK') out.recontact_6m = a.recontact_6m;
      else {
        next();
        if (a.next_by === 'PHONE_SET') {
          out.phone_start = a.phone_start;
          out.phone_with = a.phone_with;
          out.phone_number = a.phone_number;
        }
      }
      break;
    case 'HELD': {
      out.mood = a.mood;
      out.mood_note = a.mood_note;
      out.interest = a.interest;
      if (a.mood === 'DIFFICULT') out.difficulties = a.difficulties;
      if (a.interest === 'LIKELY' || a.interest === 'UNDECIDED') {
        out.missing = a.missing;
        out.follow_up_at = a.follow_up_at;
        out.follow_up_by = a.follow_up_by;
      }
      if (a.interest === 'NONE') {
        out.no_interest_reason = a.no_interest_reason;
        out.recontact_6m = a.recontact_6m;
        if (onlyPrice(a)) out.facts = { price: a.facts?.price };
      }
      if (interested(a)) {
        const allowed = agreementOptions(a.interest).map((o) => o.value);
        out.facts = a.facts ?? {};
        out.agreements = (a.agreements ?? []).filter((g) => allowed.includes(g));
        if (out.agreements.includes('SECOND_APPOINTMENT')) out.second_wish = a.second_wish;
        if (out.agreements.includes('CALLBACK')) out.callback_at = a.callback_at;
      }
      break;
    }
  }
  return out;
}

// Notiz je Schritt (Dominik 12.10.2026: „in jeder Stufe möglich“): Schritte mit eigenem Textfeld nutzen es (Grund, Verlauf, Objekt,
// Team), die übrigen eine Notiz in step_notes. Die KI wertet alle Notizen aus.
type NoteTarget = { kind: 'step'; key: 'held' | 'noShow' | 'interest' | 'agreements' } | { kind: 'field'; key: 'not_possible_note' | 'mood_note' | 'note' } | { kind: 'condition' };
export const noteTargets: Record<StepKey, NoteTarget & { label: string; placeholder: string }> = {
  held: { kind: 'step', key: 'held', label: 'Notiz (optional)', placeholder: 'z. B. besondere Situation vor Ort' },
  notPossible: { kind: 'field', key: 'not_possible_note', label: 'Was ist passiert?', placeholder: 'z. B. nur der Mieter war da' },
  noShow: { kind: 'step', key: 'noShow', label: 'Notiz (optional)', placeholder: 'z. B. Nachbar sagt: im Urlaub bis Ende Oktober' },
  mood: { kind: 'field', key: 'mood_note', label: 'Was war besonders? (optional)', placeholder: 'z. B. Ehemann entscheidet mit' },
  interest: { kind: 'step', key: 'interest', label: 'Notiz (optional)', placeholder: 'z. B. Wünsche, Provision, Laufzeit, Bedingungen' },
  facts: { kind: 'condition', label: 'Zustand, Besonderheiten, Ausstattung (optional)', placeholder: 'z. B. Garage abrissreif, Baugenehmigung vorhanden' },
  agreements: { kind: 'step', key: 'agreements', label: 'Notiz (optional)', placeholder: 'z. B. Unterlagen kommen per E-Mail' },
  summary: { kind: 'field', key: 'note', label: 'Notiz für das Team (optional)', placeholder: 'z. B. geänderte Kontaktdaten, weitere Objekte' },
};

export function noteValue(step: StepKey, a: Answers): string {
  const t = noteTargets[step];
  if (t.kind === 'step') return a.step_notes?.[t.key] ?? '';
  if (t.kind === 'condition') return a.facts?.condition ?? '';
  return a[t.key] ?? '';
}

export function withNote(step: StepKey, a: Answers, text: string): Partial<Answers> {
  const t = noteTargets[step];
  if (t.kind === 'step') return { step_notes: { ...a.step_notes, [t.key]: text } };
  if (t.kind === 'condition') return { facts: { ...a.facts, condition: text } };
  return { [t.key]: text };
}

// hasNotes: Der Bericht enthält Freitext, den die KI auswertet.
export function hasNotes(a: Answers): boolean {
  const texts = [a.note, a.mood_note, a.not_possible_note, a.cancel_reason, a.second_wish, a.facts?.condition, ...Object.values(a.step_notes ?? {})];
  return texts.some((t) => !!t?.trim());
}

// toggle: Mehrfachauswahl (Vereinbarungen, offene Punkte, Schwierigkeiten).
export function toggle<T extends string>(list: T[] | undefined, value: T): T[] {
  const set = list ?? [];
  return set.includes(value) ? set.filter((v) => v !== value) : [...set, value];
}
